import React, { useContext, useEffect, useMemo, useState } from "react";

import { Transition } from "@headlessui/react";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { AccountContext } from "@/components/Account/Account";
import ConfigContext from "@/components/Config/configContext";
import { OutletsContext } from "@/components/Outlets";

import ConnectionBar from "./ConnectionBar";
import Header from "./Header";
import LoanSharkBar from "./LoanSharkBar";
import SidebarContext from "./sidebarContext";

const LOADER_MESSAGE_COUNT = 8;

Layout.propTypes = {
  mainRef: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  pageTitle: PropTypes.string,
  beforeHeader: PropTypes.node,
  hideHeader: PropTypes.bool,
  fullScreen: PropTypes.bool,
  closeSidebarOnLoad: PropTypes.bool,
  noYOverflow: PropTypes.bool,
  stickyBar: PropTypes.node,
  noPadding: PropTypes.bool,
};

export default function Layout({
  mainRef,
  children,
  className,
  pageTitle,
  beforeHeader,
  hideHeader,
  fullScreen,
  closeSidebarOnLoad,
  noYOverflow,
  stickyBar,
  noPadding,
}) {
  const { sidebarVisible, setSidebarVisible, setSidebarFirstLoad } =
    useContext(SidebarContext);

  const { configLoading, brandConfigsLoading, merchantAppLoading } =
    useContext(ConfigContext);
  const { accountLoading } = useContext(AccountContext);
  const { outletsLoading } = useContext(OutletsContext);
  const { t } = useTranslation();

  const [slowLoad, setSlowLoad] = useState(false);
  const [slowLoadTimeout, setSlowLoadTimeout] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(-1);

  useEffect(() => {
    if (slowLoad) {
      let timer;

      timer = setInterval(() => {
        setCurrentMessageIndex((c) => c + 1);
      }, 2000);

      setCurrentMessageIndex(0);

      return () => clearInterval(timer);
    }
  }, [slowLoad]);

  useEffect(() => {
    if (closeSidebarOnLoad) {
      setSidebarFirstLoad(true);
      setSidebarVisible(false);
      setTimeout(() => {
        setSidebarVisible(true);
        setTimeout(() => {
          setSidebarVisible(false);
        }, 500);
      }, 0);
    }
  }, [closeSidebarOnLoad]);

  let klass = "h-screen flex overflow-hidden layout w-full bg-amp-gray";
  if (className) {
    klass += ` ${className}`;
  }

  const progress = useMemo(
    () =>
      [
        configLoading,
        brandConfigsLoading,
        merchantAppLoading,
        accountLoading,
        outletsLoading,
      ].filter((n) => !n).length * 20,
    [
      configLoading,
      brandConfigsLoading,
      merchantAppLoading,
      accountLoading,
      outletsLoading,
    ],
  );

  useEffect(() => {
    if (progress === 0) {
      setSlowLoadTimeout(
        setTimeout(() => {
          setSlowLoad(true);
        }, 5000),
      );
    } else if (slowLoadTimeout && progress === 100) {
      clearTimeout(slowLoadTimeout);
      setSlowLoad(false);
      setSlowLoadTimeout(null);
    }
  }, [progress]);

  return (
    // This example requires Tailwind CSS v2.0+
    <div className={klass}>
      <div className="flex flex-col flex-1 w-0 overflow-hidden">
        <ConnectionBar />
        <LoanSharkBar />
        {!configLoading &&
          !brandConfigsLoading &&
          !merchantAppLoading &&
          !accountLoading &&
          !outletsLoading && (
            <>
              {beforeHeader}
              {!hideHeader && (
                <Header
                  sidebarVisible={sidebarVisible}
                  setSidebarVisible={setSidebarVisible}
                >
                  <div dangerouslySetInnerHTML={{ __html: pageTitle }} />
                </Header>
              )}
              {stickyBar && (
                <div className="sticky top-0 z-30 bg-white">{stickyBar}</div>
              )}
            </>
          )}
        <main
          ref={mainRef}
          className={`relative flex-1 ${
            noYOverflow ?? "overflow-y-auto"
          } focus:outline-none`}
          tabIndex="0"
        >
          <Transition
            show={
              !!configLoading ||
              !!brandConfigsLoading ||
              !!merchantAppLoading ||
              !!accountLoading ||
              !!outletsLoading
            }
            className="fixed top-0 bottom-0 left-0 right-0 flex flex-col items-center justify-center w-full h-full space-y-2 duration-300 bg-white z-90"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <div className="flex flex-row items-center justify-center space-x-2 text-lg font-semibold">
              <ArrowPathIcon className="w-6 h-6 animate-spin" />
              <div>{t("common.loading")}</div>
            </div>
            <div className="relative flex items-center justify-center w-[300px] h-[32px] bg-gray-200 rounded-full dark:bg-gray-300 p-1">
              {progress > 0 && (
                <div
                  className="absolute animate-pulse left-1 top-1 h-[25px] bg-indigo-600 text-xs font-medium text-blue-100 text-center leading-none rounded-full transition-all"
                  style={{ width: `calc(${progress}% - 8px)` }}
                />
              )}
              <div className="relative z-10 flex m-auto text-white">{`${progress}%`}</div>
            </div>
            <div
              className={classNames("relative w-[300px]", {
                "opacity-0": !slowLoad,
              })}
            >
              {[...Array(LOADER_MESSAGE_COUNT)].map((_, index) => {
                return (
                  <Transition
                    key={index}
                    show={currentMessageIndex % LOADER_MESSAGE_COUNT === index}
                    className="absolute top-0 left-0 right-0 text-center"
                    enter="transition ease-in-out duration-500 transform"
                    enterFrom="opacity-0 -translate-x-24"
                    enterTo="opacity-100 translate-x-0"
                    leave="transition ease-in-out duration-500 transform"
                    leaveFrom="opacity-100 translate-x-0"
                    leaveTo="opacity-0 translate-x-24"
                  >
                    {t(`common.slowLoad.${index}`)}
                  </Transition>
                );
              })}
            </div>
          </Transition>
          <div
            className={classNames(
              fullScreen ? "h-full w-full" : noPadding ? undefined : "p-4",
              {
                hidden:
                  configLoading ||
                  brandConfigsLoading ||
                  merchantAppLoading ||
                  accountLoading ||
                  outletsLoading,
              },
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
